import * as React from 'react';
import { cn } from '@/lib/utils';

export type MarginNoteTone = 'ink' | 'teal' | 'coral' | 'race';

export interface MarginNoteProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Caveat handwritten margin note. Used to ANNOTATE — never to decorate. */
  tilt?: number;
  tone?: MarginNoteTone;
  size?: 'sm' | 'md' | 'lg';
}

const toneClass: Record<MarginNoteTone, string> = {
  ink:   'text-fd-text',
  teal:  'text-fd-teal',
  coral: 'text-fd-coral',
  race:  'text-fd-accent',
};

const sizeClass: Record<NonNullable<MarginNoteProps['size']>, string> = {
  sm: 'text-[18px] leading-[1.05]',
  md: 'text-[24px] leading-[1.05]',
  lg: 'text-[30px] leading-[1.05]',
};

/**
 * MarginNote — handwritten Caveat margin note in the workshop-note style.
 * Limited tones (ink default, teal guidance, coral friction, race brand).
 * Discipline: max 3 scribble moments per surface; this never decorates.
 */
export const MarginNote = React.forwardRef<HTMLSpanElement, MarginNoteProps>(
  ({ tilt = -3, tone = 'ink', size = 'md', className, style, ...rest }, ref) => (
    <span
      ref={ref}
      className={cn(
        'inline-block font-caveat font-medium tracking-normal',
        toneClass[tone],
        sizeClass[size],
        className,
      )}
      style={{ transform: `rotate(${tilt}deg)`, transformOrigin: 'center', ...style }}
      {...rest}
    />
  ),
);
MarginNote.displayName = 'MarginNote';
